import { dispositionBucket, isQueueCall } from '@/utils/callRecords';
import { partyText } from '@/lib/reports/callCostReport';
import { recordTimeMs, ringTime } from '@/lib/reports/dailyActivityReport';
import type { CallRecord, CallSummary } from '@/types';

/** Plain-English answers for the call detail view in the Today's Stats pop-ups. */

/** Every CDR row of the same call (same uid), oldest first; just the row itself without a uid. */
export function callLegs(record: CallRecord, records: CallRecord[]): CallRecord[] {
  if (!record.uid) return [record];
  const legs = records.filter((other) => other.uid === record.uid);
  if (!legs.includes(record)) legs.push(record);
  return legs.sort((a, b) => recordTimeMs(a) - recordTimeMs(b));
}

/** "CQ-SelfieBox (670)" from "Queue CQ-SelfieBox<670>"; "102-Cape Town" when the name has the number. */
export function placeText(value?: string) {
  const raw = String(value || '')
    .replace(/^(Queue|IVR|Ring Group|RingGroup)\s+/i, '')
    // CDR v2 writes an agent as "O-Support:Nhlanhla Motau<104>"
    .replace(/^[^<>:]+:(?=[^<>]*<)/, '')
    .trim();
  const match = raw.match(/^(.*?)\s*<([^>]*)>\s*$/);
  if (match && match[1] && match[2] && match[1].includes(match[2])) return match[1];
  return partyText(raw);
}

/** Who hung up, from CDR v2's disconnected_by (External is the outside party). */
export function endedByText(disconnectedBy: string | undefined, callType: CallRecord['call_type']) {
  switch (String(disconnectedBy || '').toLowerCase()) {
    case 'external':
      return callType === 'Outbound' ? 'The person called' : callType === 'Inbound' ? 'The caller' : 'The other party';
    case 'extension':
      return callType === 'Outbound' ? 'Your extension (the caller)' : 'Your extension';
    case 'system':
      return 'The phone system';
    default:
      return undefined;
  }
}

const STATUS_TEXT: Record<string, string> = {
  ANSWERED: 'Answered',
  'NO ANSWER': 'No answer',
  ABANDONED: 'Caller hung up',
  BUSY: 'Busy',
  VOICEMAIL: 'Voicemail',
  FAILED: 'Failed',
};

export function statusText(status: string) {
  return STATUS_TEXT[status.toUpperCase()] || status;
}

function where(record: CallRecord) {
  return isQueueCall(record) ? `waiting in the ${placeText(record.call_to)} queue` : `while it rang at ${placeText(record.call_to)}`;
}

/**
 * One or two sentences on what happened to the call. `record` is the row that was clicked;
 * `summary` (CDR v2, when the PBX has it) says how the whole call ended and who hung up.
 */
export function describeCall(record: CallRecord, legs: CallRecord[], summary: CallSummary | null): string {
  const bucket = dispositionBucket(record);
  const ring = ringTime(record.ring_duration || 0);
  const status = (summary?.status || String(record.disposition || '')).toUpperCase();
  const endedBy = String(summary?.disconnectedBy || '').toLowerCase();
  // The person who answered in the end: an extension rather than the queue it came through
  const answeredLegs = legs.filter(
    (leg) =>
      leg !== record &&
      dispositionBucket(leg) === 'answered' &&
      recordTimeMs(leg) >= recordTimeMs(record) &&
      !/^IVR\s/i.test(String(leg.call_to || ''))
  );
  const answeredLeg = answeredLegs.filter((leg) => !isQueueCall(leg)).pop() ?? answeredLegs.pop();
  const later = legs.filter((leg) => recordTimeMs(leg) > recordTimeMs(record) && leg !== record);

  if (/voicemail/i.test(String(record.disposition))) {
    return `Nobody answered, so the call went to ${placeText(record.call_to)}.`;
  }
  if (bucket === 'answered') {
    const by = isQueueCall(record) ? answeredLeg?.call_to || summary?.lastParticipant || record.call_to : record.call_to;
    const talk = record.talk_duration ?? summary?.handlingDuration ?? 0;
    return `Answered by ${placeText(by)} after ${ringTime(record.ring_duration || 0)}, with ${ringTime(talk)} of talk time.`;
  }

  const notHere =
    status === 'BUSY' && bucket === 'busy'
      ? `${placeText(record.call_to)} was busy.`
      : `Not answered ${isQueueCall(record) ? `in the ${placeText(record.call_to)} queue` : `at ${placeText(record.call_to)}`}${record.ring_duration ? ` (rang ${ring})` : ''}.`;

  if (answeredLeg || status === 'ANSWERED') {
    const by = answeredLeg?.call_to || summary?.lastParticipant;
    return `${notHere} The call then moved on and was answered${by ? ` by ${placeText(by)}` : ''}.`;
  }
  if (status === 'VOICEMAIL' || later.some((leg) => /voicemail/i.test(`${leg.disposition} ${leg.call_to}`))) {
    return `${notHere} The caller was sent to voicemail.`;
  }
  if (status === 'ABANDONED' || (summary && endedBy === 'external')) {
    return `The caller hung up after ${ring} ${where(record)}, before anyone answered.`;
  }
  if (status === 'BUSY' || bucket === 'busy') return `${placeText(record.call_to)} was busy, so the call could not get through.`;
  if (status === 'FAILED' || bucket === 'failed') return 'The call could not be connected.';
  if (endedBy === 'system') {
    return `Nobody answered ${where(record)}; the phone system ended the call after ${ring} (the ring time ran out).`;
  }
  if (later.length) return `${notHere} The call then went on to ${placeText(later[later.length - 1].call_to)}.`;
  return notHere;
}
